import { Component, EventEmitter, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { AshcorpFileUploadConfig } from './file-upload.model';

@Component({
    selector: 'ashcorp-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AshcorpFileUploadComponent {
    @Input() form!: FormGroup;
    @Input() config!: AshcorpFileUploadConfig;
    @Output() uploadCompleted: EventEmitter<string> = new EventEmitter<string>();

    selectedFileName = '';
    isDragging = false;
    isUploading = false;
    localPreviewUrl = '';

    get id(): string {
        return `file-upload-${this.name || this.label || 'field'}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    get label(): string {
        return this.config?.label || this.config?.name || '';
    }

    get name(): string {
        return this.config?.name || '';
    }

    get required(): boolean {
        return !!this.config?.required;
    }

    get readonly(): boolean {
        return !!this.config?.readonly;
    }

    get accept(): string {
        return this.config?.accept || '*/*';
    }

    get buttonLabel(): string {
        return this.config?.buttonLabel || 'Choose file';
    }

    get multiple(): boolean {
        return !!this.config?.multiple;
    }

    get enableDropzone(): boolean {
        return this.config?.enableDropzone !== false;
    }

    get isImage(): boolean {
        return !!this.config?.isImage;
    }

    get previewUrl(): string {
        return this.config?.previewUrl || '';
    }

    get emptyDropText(): string {
        return this.config?.emptyDropText || 'Drag and drop a file here, or click to browse';
    }

    get selectedDropText(): string {
        return this.config?.selectedDropText || 'Drag and drop another file, or click to replace';
    }

    get hasPreview(): boolean {
        return this.isImage && !!(this.localPreviewUrl || this.previewUrl);
    }

    get resolvedPreviewUrl(): string {
        return this.localPreviewUrl || this.previewUrl;
    }

    get control(): AbstractControl | null {
        if (!this.form || !this.name) {
            return null;
        }

        return this.form.get(this.name);
    }

    get errorMessage(): string {
        const control = this.control;
        if (!control) {
            return '';
        }

        const serverError = control.getError('server');
        if (serverError) {
            return serverError;
        }

        if (control.hasError('required')) {
            return `${this.label || this.name} is required.`;
        }

        return '';
    }

    openPicker(input: HTMLInputElement): void {
        if (this.readonly) {
            return;
        }

        input.click();
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = input.files;

        if (!files || files.length === 0) {
            return;
        }

        this._setSelectedFiles(Array.from(files));

        // Reset file input so selecting the same file again still fires change.
        input.value = '';
    }

    onDragOver(event: DragEvent): void {
        if (this.readonly || !this.enableDropzone) {
            return;
        }

        event.preventDefault();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        if (this.readonly || !this.enableDropzone) {
            return;
        }

        event.preventDefault();
        this.isDragging = false;
    }

    onDrop(event: DragEvent): void {
        if (this.readonly || !this.enableDropzone) {
            return;
        }

        event.preventDefault();
        this.isDragging = false;

        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) {
            return;
        }

        this._setSelectedFiles(Array.from(files));
    }

    clearSelection(): void {
        this._clearLocalPreview();
        this.selectedFileName = '';
        this.control?.setValue(null);
        this.control?.markAsDirty();
        this.control?.markAsTouched();
        this._clearControlError();
    }

    ngOnDestroy(): void {
        this._clearLocalPreview();
    }

    private _setSelectedFiles(selected: File[]): void {
        this.selectedFileName = selected.map((file) => file.name).join(', ');

        if (this.isImage && selected[0]) {
            this._clearLocalPreview();
            this.localPreviewUrl = URL.createObjectURL(selected[0]);
        }

        this.control?.markAsDirty();
        this.control?.markAsTouched();
        this._upload(selected[0]);
    }

    private _upload(file: File | undefined): void {
        if (!file || !this.config?.upload) {
            return;
        }

        if (!this._isAcceptedFile(file)) {
            this._setControlError(this.config?.invalidTypeMessage || 'Selected file type is not allowed.');
            return;
        }

        this._clearControlError();
        this.isUploading = true;

        this.config.upload(file)
            .pipe(
                finalize(() => {
                    this.isUploading = false;
                })
            )
            .subscribe({
                next: (path: string) => {
                    if (!path) {
                        this._setControlError(this.config?.noPathMessage || 'Upload completed but no file path was returned.');
                        return;
                    }

                    this.control?.setValue(path);
                    this.control?.markAsDirty();
                    this.control?.markAsTouched();
                    this.uploadCompleted.emit(path);
                },
                error: () => {
                    this._setControlError(this.config?.uploadFailedMessage || 'Unable to upload selected file.');
                }
            });
    }

    private _isAcceptedFile(file: File): boolean {
        const accepted = this.accept.split(',').map((part) => part.trim()).filter((part) => !!part);

        if (accepted.length === 0 || this.accept === '*/*') {
            return true;
        }

        return accepted.some((rule) => {
            if (rule.endsWith('/*')) {
                const prefix = rule.replace('/*', '/');
                return file.type.startsWith(prefix);
            }

            if (rule.startsWith('.')) {
                return file.name.toLowerCase().endsWith(rule.toLowerCase());
            }

            return file.type === rule;
        });
    }

    private _setControlError(message: string): void {
        const control = this.control;
        if (!control) {
            return;
        }

        const errors = { ...(control.errors || {}) };
        errors.server = message;
        control.setErrors(errors);
        control.markAsTouched();

        this.config?.onError?.(message);
    }

    private _clearControlError(): void {
        const control = this.control;
        if (!control || !control.errors?.server) {
            return;
        }

        const errors = { ...(control.errors || {}) };
        delete errors.server;
        control.setErrors(Object.keys(errors).length ? errors : null);
    }

    private _clearLocalPreview(): void {
        if (this.localPreviewUrl) {
            URL.revokeObjectURL(this.localPreviewUrl);
            this.localPreviewUrl = '';
        }
    }
}
